import { useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/heroSlides";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);
  const total = heroSlides.length;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [total]);

  const go = (next: number) => setIndex((next + total) % total);

  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) < 48) return;
    go(index + (delta < 0 ? 1 : -1));
  };

  const slide = heroSlides[index];

  return (
    <section
      className="relative h-[520px] w-full overflow-hidden bg-primary-950 md:h-[640px]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="热门影视焦点推荐"
    >
      {heroSlides.map((item, slideIndex) => (
        <img
          key={item.id}
          src={item.backdrop}
          alt={`${item.title}影视剧照背景`}
          title={`${item.title} - 中文字幕在线观看`}
          className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-700 ${
            slideIndex === index ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/10 to-primary-950/50"></div>

      <div className="relative flex h-full items-end px-4 pb-14 md:items-center md:px-8 md:pb-0">
        <div className="w-full max-w-[720px]">
          <div key={slide.id} className="animate-fade-up">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-accent-400/50 bg-accent-500/15 px-3 py-1 text-[11px] text-accent-300 md:text-xs">
              <i className="ri-fire-fill text-sm" aria-hidden="true"></i>
              {slide.badge}
            </span>

            <h1 className="mt-4 font-heading text-3xl font-semibold leading-tight text-background-50 md:text-5xl">
              <span className="mb-3 block text-[11px] font-normal tracking-[0.22em] text-accent-400 md:text-xs">
                中文字幕在线免费看 · 高清中文字幕影视
              </span>
              {slide.title}
            </h1>

            <p className="mt-4 max-w-[560px] text-[13px] leading-7 text-background-200 md:text-sm md:leading-8">
              {slide.subtitle}
            </p>

            <p className="mt-3 text-xs text-background-300">{slide.meta}</p>

            <div className="mt-6 flex flex-wrap items-center gap-2.5">
              <Link
                to={`/detail/${slide.targetId}`}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-fill text-base" aria-hidden="true"></i>
                立即观看
              </Link>
              <Link
                to={slide.targetId === "chang-ye-jiang-ming" ? "/category/tv" : "/category/movie"}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-50/35 px-5 py-2.5 text-sm text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
              >
                浏览同类作品
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 left-4 flex items-center gap-2 md:left-8">
        {heroSlides.map((item, dotIndex) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到${item.title}`}
            onClick={() => go(dotIndex)}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              dotIndex === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/45 hover:bg-background-50/70"
            }`}
          ></button>
        ))}
      </div>

      <div className="absolute bottom-4 right-4 hidden items-center gap-2 md:flex md:right-8">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(index - 1)}
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
        >
          <i className="ri-arrow-left-s-line text-lg" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(index + 1)}
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
        >
          <i className="ri-arrow-right-s-line text-lg" aria-hidden="true"></i>
        </button>
      </div>
    </section>
  );
}